/**
 * Framework Hero Component
 * Displays framework title, compliance status, and active cycles
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, AlertCircle, Calendar, Shield } from 'lucide-react';

export default function FrameworkHero({ framework, coveragePercentage, activeCycles }) {
  // Calculate next deadline
  const nextDeadline = activeCycles && activeCycles.length > 0
    ? new Date(activeCycles[0].filingDeadline)
    : null;
  
  const daysUntilDeadline = nextDeadline
    ? Math.ceil((nextDeadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  // Determine status
  const getStatusConfig = () => {
    if (coveragePercentage >= 80) {
      return { icon: CheckCircle2, text: 'On track for compliance', colorClass: 'text-emerald-400' };
    } else if (coveragePercentage >= 50) {
      return { icon: Clock, text: 'In progress', colorClass: 'text-amber-400' };
    }
    return { icon: AlertCircle, text: 'Action required', colorClass: 'text-red-400' };
  };

  const statusConfig = getStatusConfig();
  const StatusIcon = statusConfig.icon;

  const totalDataPoints = framework?.requirements_summary?.total_data_points || 1144;
  const coveredCount = Math.round((coveragePercentage / 100) * totalDataPoints);

  return (
    <div className="bg-gradient-to-br from-[#001a4d] via-[#002266] to-[#003399] rounded-xl p-8" data-testid="framework-hero">
      {/* Framework Title */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-3">
          <h1 className="text-3xl font-bold text-white">
            {framework?.framework_short_name || 'CSRD/ESRS'}
          </h1>
          <span className="px-3 py-1 text-xs font-bold uppercase tracking-wider bg-white/20 text-white rounded-full">
            {framework?.framework_type || 'Mandatory'}
          </span>
        </div>
        <p className="text-blue-100 text-lg max-w-3xl">
          {framework?.framework_name || 'Corporate Sustainability Reporting Directive (ESRS)'}
        </p>
        <div className="flex items-center gap-6 mt-4 text-sm text-blue-200">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 size={16} />
            {totalDataPoints.toLocaleString()} Data Points
          </span>
          <span className="text-blue-300">•</span>
          <span className="flex items-center gap-1.5">
            <Calendar size={16} />
            Wave 1: 2024
          </span>
          <span className="text-blue-300">•</span>
          <span className="flex items-center gap-1.5">
            <Shield size={16} />
            Limited Assurance
          </span>
        </div>
      </div>

      {/* Compliance Status Card */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Progress */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-3">Your Compliance Status</h3>
            <div className="space-y-3">
              {/* Progress Bar */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/80">Overall Coverage</span>
                  <span className="text-2xl font-bold text-white">{coveragePercentage}%</span>
                </div>
                <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                    style={{ width: `${coveragePercentage}%` }}
                  />
                </div>
                <p className="text-xs text-white/70 mt-2">
                  {coveredCount.toLocaleString()} / {totalDataPoints.toLocaleString()} requirements covered
                </p>
              </div>

              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                <StatusIcon size={20} className={statusConfig.colorClass} />
                <span className="text-sm text-white/90">{statusConfig.text}</span>
              </div>
            </div>
          </div>

          {/* Right: Active Cycles */}
          <div>
            <h3 className="text-sm font-semibold text-white/90 mb-3">Active Disclosure Cycles</h3>
            {activeCycles && activeCycles.length > 0 ? (
              <div className="space-y-2">
                {activeCycles.slice(0, 2).map(cycle => (
                  <Link
                    key={cycle.id}
                    to={`/cycles/${cycle.id}`}
                    className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
                    data-testid={`cycle-link-${cycle.id}`}
                  >
                    <p className="text-sm font-medium text-white">{cycle.name}</p>
                    <p className="text-xs text-white/70 mt-1">
                      Deadline: {new Date(cycle.filingDeadline).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </Link>
                ))}

                {activeCycles.length > 2 && (
                  <p className="text-xs text-white/60 text-center pt-1">
                    +{activeCycles.length - 2} more cycle{activeCycles.length - 2 > 1 ? 's' : ''}
                  </p>
                )}

                {nextDeadline && daysUntilDeadline !== null && (
                  <div className={`mt-3 p-2 rounded-lg text-center ${
                    daysUntilDeadline < 30 
                      ? 'bg-red-500/20 text-red-200' 
                      : 'bg-white/10 text-white/80'
                  }`}>
                    <p className="text-xs font-medium">
                      Next Deadline: {daysUntilDeadline} days
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <p className="text-sm text-white/60 mb-3">No active cycles for this framework</p>
                <Link
                  to="/cycles/active"
                  className="text-sm text-white underline hover:text-white/80"
                >
                  Create a disclosure cycle →
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
